import { Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { useTokenStore } from '@/stores/token';
import { CommonResType } from '@/types/commonApiType';

import { api } from './_http';

// 아이디 중복 확인(가입시)
type CheckIdDuplicateDataType = {
	member_id: string;
};
type CheckIdDuplicateResType = CommonResType;
export const useCheckIdDuplicateApi = () => {
	const url = '/v1/front/checkDuplicateId';
	return useMutation<AxiosResponse<CheckIdDuplicateResType>, AxiosError, CheckIdDuplicateDataType>({
		mutationFn: (data: CheckIdDuplicateDataType) => api.post(url, data),
	});
};
type RequestTokenDataType = {
	refresh_token: string;
};
type RequestTokenResType = CommonResType;
export const useRequestTokenApi = () => {
	const url = '/v1/front/requestToken';
	return useMutation<AxiosResponse<RequestTokenResType>, AxiosError, RequestTokenDataType>({
		mutationFn: (data: RequestTokenDataType) => api.post(url, data),
	});
};
// 닉네임 중복 확인(가입시)
type CheckDuplicateNicknameDataType = {
	member_nickname: string;
};
type CheckDuplicateNicknameResType = CommonResType;
export const useCheckDuplicateNicknameApi = () => {
	const url = '/v1/front/checkDuplicateNickname';
	return useMutation<AxiosResponse<CheckDuplicateNicknameResType>, AxiosError, CheckDuplicateNicknameDataType>({
		mutationFn: (data: CheckDuplicateNicknameDataType) => api.post(url, data),
	});
};

// 회원가입-인증번호 요청
type SendAuthDataType = {
	member_contact: string;
	member_name?: string;
};
type SendAuthResType = CommonResType;
export const useSendAuthApi = () => {
	const url = '/sendSms';
	return useMutation<AxiosResponse<SendAuthResType>, AxiosError, SendAuthDataType>({
		mutationFn: (data: SendAuthDataType) => api.post(url, data),
	});
};

// 회원가입 제외-인증번호 확인
type CheckIdentificationNumbeDataType = {
	phonenum: string;
	identification_no: string;
};
type CheckIdentificationNumbeResType = CommonResType;
export const useCheckIdentificationNumberApi = () => {
	const url = '/v1/front/checkIdentificationNumber';
	return useMutation<AxiosResponse<CheckIdentificationNumbeResType>, AxiosError, CheckIdentificationNumbeDataType>({
		mutationFn: (data: CheckCertificationDataType) => api.post(url, data),
	});
};

// 회원가입-인증번호 확인
type CheckCertificationDataType = {
	phonenum: string;
	identification_no: string;
};
type CheckCertificationResType = CommonResType;
export const useCheckCertificationApi = () => {
	const url = '/v1/front/checkIdentificationNumberForJoin';
	return useMutation<AxiosResponse<CheckCertificationResType>, AxiosError, CheckCertificationDataType>({
		mutationFn: (data: CheckCertificationDataType) => api.post(url, data),
	});
};

// 회원가입-추천인 코드 확인
type CheckRecommendCodeDataType = {
	recommend_code: string;
};
type CheckRecommendCodeResType = CommonResType;
export const useCheckRecommendCodeApi = () => {
	const url = '/v1/front/checkRecommendCode';
	return useMutation<AxiosResponse<CheckRecommendCodeResType>, AxiosError, CheckRecommendCodeDataType>({
		mutationFn: (data: CheckRecommendCodeDataType) => api.post(url, data),
	});
};
// 일반 회원가입
export type SignupDataType = {
	access_token?: string;
	member_name: string;
	member_nickname: string;
	member_contact: string;
	member_join_path: string;
	member_push_agree: string;
	member_id?: string;
	member_password?: string;
	recommend_code?: string;
	identification_no?: string;
};
type SignupResType = CommonResType<{
	access_token: string;
	refresh_token: string;
}>;
export const useSignupApi = () => {
	const url = '/v1/front/requestSignup';
	return useMutation<AxiosResponse<SignupResType>, AxiosError, SignupDataType>({
		mutationFn: (data: SignupDataType) => api.post(url, data),
	});
};

// 비밀번호 찾기
type FindMemberPasswordDataType = {
	member_contact: string;
	member_id: string;
};
type FindMemberPasswordResType = CommonResType;
export const useFindMemberPasswordApi = () => {
	const url = '/v1/front/findMemberPassword';
	return useMutation<AxiosResponse<FindMemberPasswordResType>, AxiosError, FindMemberPasswordDataType>({
		mutationFn: (data: FindMemberPasswordDataType) => api.post(url, data),
	});
};

// 비밀번호 변경
type ResetMemberPasswordDataType = {
	member_id: string;
	member_password: string;
};
type ResetMemberPasswordResType = CommonResType;
export const useResetMemberPasswordApi = () => {
	const url = '/v1/front/resetMemberPassword';
	return useMutation<AxiosResponse<ResetMemberPasswordResType>, AxiosError, ResetMemberPasswordDataType>({
		mutationFn: (data: ResetMemberPasswordDataType) => api.post(url, data),
	});
};

// 로그인
type LoginDataType = {
	id: string;
	password: string;
	device_type?: string;
};
type LoginResType = CommonResType<{
	access_token: string;
	refresh_token: string;
	member_nickname: string;
	group_name: string | number;
	group_pk: number | null;
	group_sp: number | null;
	member_join_path: string;
	member_name: string;
}>;
export const useLoginApi = () => {
	const url = '/v1/front/signin';
	const queryClient = useQueryClient();
	const tokenStore = useTokenStore();
	
	return useMutation<AxiosResponse<LoginResType>, AxiosError, LoginDataType>({
		mutationFn: (data: LoginDataType) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				queryClient.clear(); // 로그인시 이전의 모든 캐시 삭제
				tokenStore.setToken(res.data.data.access_token, res.data.data.refresh_token); // 토큰 저장
			}
		},
	});
};

// 소셜 로그인 검증
type RequestTSignParamType = {
	// COMMENT : 소셜 로그인 소통하면서 변경할 예정입니다
	token: string;
	device_type?: string;
};
type RequestTSignResType = {
	success: true;
	data: {
		member_pk: number;
		member_name: string;
		member_push_yn: string;
		access_token: string;
		refresh_token: string;
		member_nickname: string;
		basic_info_yn: boolean;
		member_join_path: string;
		group_pk: number | null;
		group_sp: number | null;
	};
};

export const useRequestTSignApi = () => {
	const url = '/v1/front/requestTSign';
	return useMutation<AxiosResponse<RequestTSignResType>, AxiosError, RequestTSignParamType>({
		mutationFn: (data: RequestTSignParamType) => api.post(url, data),
	});
};

// 아이디 찾기
type FindMemberIdDataType = {
	phonenum: string;
};
type FindMemberIdResType = CommonResType<{
	member_id: { member_id: string };
}>;
export const useFindMemberIdApi = () => {
	const url = '/v1/front/findMemberId';
	return useMutation<AxiosResponse<FindMemberIdResType>, AxiosError, FindMemberIdDataType>({
		mutationFn: (data: FindMemberIdDataType) => api.post(url, data),
	});
};

// 권한 확인
type GetCheckAuthDataType = {
	num: string | 'ALL';
	student: boolean;
	enable?: boolean;
};

type GetCheckAuthResType = CommonResType<{
	member_group_name: string;
	member_group_telegram_link: string;
}>;
export const useGetCheckAuthApi = (params: Ref<GetCheckAuthDataType>) => {
	const url = '/v1/front/getCheckAuth';
	
	return useQuery<AxiosResponse, AxiosError, GetCheckAuthResType>({
		queryKey: [url, params],
		queryFn: () => api.get(url, { num: params.value.num, student: params.value.student }),
		select: (res) => res.data,
		enabled: params.value.enable ?? false,
	});
};

type GetCheckTokenResType = CommonResType;
export const useGetCheckTokenApi = () => {
	const url = '/v1/front/checkToken';
	return useQuery<AxiosResponse, AxiosError, GetCheckTokenResType>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => {
			return res.data;
		},
		enabled: false,
	});
};
